'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  id: number;
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

const stats: Stat[] = [
  {
    id: 1,
    value: 500,
    suffix: "+",
    label: "Projets Réalisés",
    icon: "CheckBadgeIcon"
  },
  {
    id: 2,
    value: 7,
    suffix: " ans",
    label: "D'Expérience",
    icon: "StarIcon"
  },
  {
    id: 3,
    value: 98,
    suffix: "%",
    label: "Clients Satisfaits",
    icon: "FaceSmileIcon"
  },
  {
    id: 4,
    value: 8,
    suffix: "",
    label: "Corps de Métiers",
    icon: "WrenchScrewdriverIcon"
  }
];

export default function StatsCounter() {
  const [counts, setCounts] = useState<number[]>(stats.map(() => 0));
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const timers = stats.map((stat, index) => {
      const increment = stat.value / steps;
      let currentStep = 0;

      return setInterval(() => {
        currentStep++;
        if (currentStep <= steps) {
          setCounts(prev => {
            const newCounts = [...prev];
            newCounts[index] = Math.min(Math.round(increment * currentStep), stat.value);
            return newCounts;
          });
        }
      }, interval);
    });

    return () => timers.forEach(timer => clearInterval(timer));
  }, [isVisible]);

  return (
    <section data-component-id="src/app/homepage/components/StatsCounter.tsx:94:4" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="94" data-component-end-line="130" data-component-file="StatsCounter.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-20%20bg-primary%20relative%20overflow-hidden%22%7D" ref={sectionRef} className="py-20 bg-primary relative overflow-hidden">
      <div data-component-id="src/app/homepage/components/StatsCounter.tsx:95:6" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="95" data-component-end-line="98" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20opacity-10%22%7D" className="absolute inset-0 opacity-10">
        <div data-component-id="src/app/homepage/components/StatsCounter.tsx:96:8" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="96" data-component-end-line="96" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-0%20left-1%2F4%20w-64%20h-64%20bg-white%20rounded-full%20blur-3xl%22%7D" className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl" />
        <div data-component-id="src/app/homepage/components/StatsCounter.tsx:97:8" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="97" data-component-end-line="97" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-0%20right-1%2F4%20w-64%20h-64%20bg-white%20rounded-full%20blur-3xl%22%7D" className="absolute bottom-0 right-1/4 w-64 h-64 bg-white rounded-full blur-3xl" />
      </div>

      <div data-component-id="src/app/homepage/components/StatsCounter.tsx:100:6" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="100" data-component-end-line="129" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20lg%3Apx-8%20relative%20z-10%22%7D" className="container mx-auto px-4 lg:px-8 relative z-10">
        <div data-component-id="src/app/homepage/components/StatsCounter.tsx:101:8" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="101" data-component-end-line="115" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20lg%3Agrid-cols-4%20gap-8%20mb-12%22%7D" className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {stats.map((stat, index) => (
            <div data-component-id="src/app/homepage/components/StatsCounter.tsx:103:12" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="103" data-component-end-line="113" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" key={stat.id} className="text-center">
              <div data-component-id="src/app/homepage/components/StatsCounter.tsx:104:14" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="104" data-component-end-line="106" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20items-center%20justify-center%20w-16%20h-16%20bg-white%2F20%20backdrop-blur-sm%20rounded-full%20mb-4%22%7D" className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full mb-4">
                <Icon data-component-id="src/app/homepage/components/StatsCounter.tsx:105:16" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="105" data-component-end-line="105" data-component-file="StatsCounter.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-primary-foreground%22%7D" name={stat.icon as any} size={32} className="text-primary-foreground" />
              </div>
              <div data-component-id="src/app/homepage/components/StatsCounter.tsx:107:14" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="107" data-component-end-line="109" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-playfair%20text-5xl%20lg%3Atext-6xl%20font-bold%20text-primary-foreground%20mb-2%22%7D" className="font-playfair text-5xl lg:text-6xl font-bold text-primary-foreground mb-2">
                {counts[index]}{stat.suffix}
              </div>
              <div data-component-id="src/app/homepage/components/StatsCounter.tsx:110:14" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="110" data-component-end-line="112" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-lg%20text-primary-foreground%2F90%20font-sans%20font-semibold%22%7D" className="text-lg text-primary-foreground/90 font-sans font-semibold">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Section texte familiale */}
        <div data-component-id="src/app/homepage/components/StatsCounter.tsx:118:8" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="118" data-component-end-line="128" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20text-center%20mt-16%22%7D" className="max-w-4xl mx-auto text-center mt-16">
          <div data-component-id="src/app/homepage/components/StatsCounter.tsx:119:10" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="119" data-component-end-line="127" data-component-file="StatsCounter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%2F10%20backdrop-blur-md%20rounded-2xl%20p-8%20lg%3Ap-12%20border%20border-white%2F20%22%7D" className="bg-white/10 backdrop-blur-md rounded-2xl p-8 lg:p-12 border border-white/20">
            <h3 data-component-id="src/app/homepage/components/StatsCounter.tsx:120:12" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="120" data-component-end-line="122" data-component-file="StatsCounter.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-playfair%20text-3xl%20lg%3Atext-4xl%20font-bold%20text-primary-foreground%20mb-6%22%2C%22textContent%22%3A%22Une%20Entreprise%20Familiale%20%C3%A0%20Votre%20Service%22%7D" className="font-playfair text-3xl lg:text-4xl font-bold text-primary-foreground mb-6">
              Une Entreprise Familiale à Votre Service
            </h3>
            <p data-component-id="src/app/homepage/components/StatsCounter.tsx:123:12" data-component-path="src/app/homepage/components/StatsCounter.tsx" data-component-line="123" data-component-end-line="126" data-component-file="StatsCounter.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20lg%3Atext-xl%20text-primary-foreground%2F80%20font-sans%20leading-relaxed%22%2C%22textContent%22%3A%227%20ans%20d'exp%C3%A9rience%2C%20500%20chantiers%20cumul%C3%A9s%2C%20une%20passion%20familiale%20transmise%20de%20g%C3%A9n%C3%A9ration%20en%20g%C3%A9n%C3%A9ration.%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20Votre%20projet%20m%C3%A9rite%20notre%20excellence.%22%7D" className="text-lg lg:text-xl text-primary-foreground/80 font-sans leading-relaxed">
              7 ans d'expérience, 500 chantiers cumulés, une passion familiale transmise de génération en génération. 
              Votre projet mérite notre excellence.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}